import React, { useEffect, useState } from "react"
import CarrotClient from "carrot-api"
import { CenteredLoadingIndicator } from "components/views/molecules/Molecules"
import { useBodyClass } from "utils/Hooks"
import { RegisterError } from "components/views/fle/register/RegisterError"
import Settings from "utils/CarrotConfig"
import { NiceToSeeYou } from "components/views/fle/register/NiceToSeeYou"
import { FinancialSupport } from "components/views/fle/register/FinancialSupport"
import { Answers } from "components/views/fle/register/Answers"
import { Access } from "components/views/fle/register/Access"
import { SetPassword } from "components/views/fle/register/SetPassword"
import { AcknowledgeTerms } from "components/views/fle/register/AcknowledgeTerms"
import { HealthConsent } from "components/views/fle/register/HealthConsent"
import { EmailOptIn } from "./EmailOptIn"
import { PersonalEmail } from "./PersonalEmail"
import { regexes } from "../../../../utils/Regexes"
import { currentlySetRegistrationCohort } from "../../../context/user/UserContext"
import GuidanceInformational from "./Guidance"
import { PreferredName } from "./PreferredName"

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

enum STEPS {
  LOADING,
  INTRO,
  FINANCIAL_SUPPORT,
  ANSWERS,
  ACCESS,
  GUIDANCE_INFORMATIONAL,
  SET_PASSWORD,
  PERSONAL_EMAIL,
  PREFERRED_NAME,
  ACKNOWLEDGE_TERMS,
  HEALTH_CONSENT,
  EMAIL_OPT_IN,
  ERROR
}

const RegisterWizard = (props) => {
  const [step, setStep] = useState(STEPS.LOADING)
  const [firstName, setFirstName] = useState(null)
  const [guid, setGuid] = useState(null)
  const [email, setEmail] = useState(null)
  const [emailOptIn, setEmailOptIn] = useState(false)
  const [companyName, setCompanyName] = useState(null)
  const [coveragePercent, setCoveragePercent] = useState(null)
  const [coverageAmount, setCoverageAmount] = useState(null)
  const [currencySymbol, setCurrencySymbol] = useState(null)
  const [carrotLite, setCarrotLite] = useState(null)
  const [password, setPassword] = useState(null)
  const [readPolicy, setReadPolicy] = useState(false)
  const [readTerms, setReadTerms] = useState(false)
  const [healthConsent, setHealthConsent] = useState(false)
  const [requiresSaml, setRequiresSaml] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [benefitConfiguration, setBenefitConfiguration] = useState(null)
  const [personalEmail, setPersonalEmail] = useState(null)
  const [validEmail, setValidEmail] = useState(true)
  const [preferredName, setPreferredName] = useState(null)
  const [isUsa, setIsUsa] = useState(null)

  useBodyClass("bg-warm-gray")

  useEffect(() => {
    async function fetchRegisteringUser(): Promise<void> {
      const guidFromParams = props.match.params.guid
      setGuid(guidFromParams)
      const registeringUser = await client.getRegisteringUser(guidFromParams)
      if (!registeringUser) {
        setStep(STEPS.ERROR)
        return
      }

      setFirstName(registeringUser.firstName)
      setEmail(registeringUser.email)
      setCompanyName(registeringUser.companyName)
      setCoverageAmount(registeringUser.coverageAmount)
      setCoveragePercent(registeringUser.coveragePercent)
      setCurrencySymbol(registeringUser.currencySymbol)
      setCarrotLite(registeringUser.carrotLite)
      setRequiresSaml(registeringUser.requiresSaml)
      setBenefitConfiguration(registeringUser.benefitConfiguration)
      setIsUsa(registeringUser.isUsa)
      setEmailOptIn(!!registeringUser.isUsa)
      setStep(STEPS.INTRO)
    }
    fetchRegisteringUser()
  }, [props.match.params.guid])

  async function createUser(): Promise<void> {
    if (!isSubmitting) {
      try {
        setIsSubmitting(true)
        const registrationCohort = currentlySetRegistrationCohort
        await client.createAccount(
          guid,
          password,
          emailOptIn,
          readTerms && readPolicy && healthConsent,
          personalEmail,
          registrationCohort,
          preferredName
        )
      } catch {
        window.location.href = "/"
      }
      window.location.href = "/registration/complete"
      /*
       *  The usage of window.location.href over 'history' is intentional.
       *  This is due to how we determine if a user is logged or not, and
       *  we want to trigger a server refresh to force our app to see that
       *  we're now logged in.
       */
    }
  }

  function onCompleteSetPassword(password?: string): void {
    setPassword(password)
    setStep(STEPS.PERSONAL_EMAIL)
  }

  function isValidEmail(email?: string): boolean {
    if (email == null || email === "") {
      return true
    }

    return regexes.email.test(email) && email.length === email.trim().length
  }

  function onCompleteSetPersonalEmail(): void {
    if (isValidEmail(personalEmail)) {
      setValidEmail(true)
      setStep(STEPS.PREFERRED_NAME)
    } else {
      setValidEmail(false)
    }
  }

  function onSkipNullOutPersonalEmail(): void {
    setPersonalEmail(null)
    setStep(STEPS.PREFERRED_NAME)
  }

  switch (step) {
    case STEPS.INTRO:
      return (
        <NiceToSeeYou
          companyName={companyName}
          firstName={firstName}
          onComplete={() => setStep(carrotLite ? STEPS.ANSWERS : STEPS.FINANCIAL_SUPPORT)}
        />
      )
    case STEPS.FINANCIAL_SUPPORT:
      return (
        <FinancialSupport
          companyName={companyName}
          coverageAmount={coverageAmount}
          coveragePercent={coveragePercent}
          currencySymbol={currencySymbol}
          onBack={() => setStep(STEPS.INTRO)}
          onComplete={() => setStep(STEPS.ANSWERS)}
        />
      )
    case STEPS.ANSWERS:
      return (
        <Answers
          onBack={() => setStep(carrotLite ? STEPS.INTRO : STEPS.FINANCIAL_SUPPORT)}
          onComplete={() => setStep(STEPS.ACCESS)}
          benefitConfiguration={benefitConfiguration}
        />
      )
    case STEPS.ACCESS:
      return <Access onBack={() => setStep(STEPS.ANSWERS)} onComplete={() => setStep(STEPS.GUIDANCE_INFORMATIONAL)} />
    case STEPS.GUIDANCE_INFORMATIONAL:
      return (
        <GuidanceInformational
          carrotLite={carrotLite}
          onBack={() => setStep(STEPS.ACCESS)}
          onComplete={() => setStep(requiresSaml ? STEPS.PERSONAL_EMAIL : STEPS.SET_PASSWORD)}
        />
      )
    case STEPS.SET_PASSWORD:
      return (
        <SetPassword
          email={email}
          onBack={() => setStep(STEPS.GUIDANCE_INFORMATIONAL)}
          onComplete={onCompleteSetPassword}
        />
      )
    case STEPS.PERSONAL_EMAIL:
      return (
        <PersonalEmail
          personalEmail={personalEmail || ""}
          setPersonalEmail={setPersonalEmail}
          validEmail={validEmail}
          onBack={() => setStep(requiresSaml ? STEPS.ACCESS : STEPS.SET_PASSWORD)}
          onComplete={onCompleteSetPersonalEmail}
          onSkip={onSkipNullOutPersonalEmail}
        />
      )
    case STEPS.PREFERRED_NAME:
      return (
        <PreferredName
          {...{ setPreferredName, preferredName }}
          onBack={() => setStep(STEPS.PERSONAL_EMAIL)}
          onComplete={() => setStep(STEPS.ACKNOWLEDGE_TERMS)}
        />
      )
    case STEPS.ACKNOWLEDGE_TERMS:
      return (
        <AcknowledgeTerms
          {...{ firstName, preferredName, readPolicy, setReadPolicy, readTerms, setReadTerms }}
          onBack={() => setStep(STEPS.PREFERRED_NAME)}
          onComplete={() => setStep(STEPS.HEALTH_CONSENT)}
          benefitConfiguration={benefitConfiguration}
        />
      )
    case STEPS.HEALTH_CONSENT:
      return (
        <HealthConsent
          isSubmitting={isSubmitting}
          healthConsent={healthConsent}
          setHealthConsent={setHealthConsent}
          benefitConfiguration={benefitConfiguration}
          onBack={() => setStep(STEPS.ACKNOWLEDGE_TERMS)}
          onComplete={isUsa ? createUser : () => setStep(STEPS.EMAIL_OPT_IN)}
        />
      )
    case STEPS.EMAIL_OPT_IN:
      return (
        <EmailOptIn
          emailOptIn={emailOptIn}
          setEmailOptIn={setEmailOptIn}
          benefitConfiguration={benefitConfiguration}
          onBack={() => setStep(STEPS.HEALTH_CONSENT)}
          onComplete={createUser}
        />
      )
    case STEPS.ERROR:
      return <RegisterError />
    default:
      return (
        <div className="vh-100 flex items-center justify-center">
          <CenteredLoadingIndicator />
        </div>
      )
  }
}

export default RegisterWizard
